import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Typography, Spin, Grid, Tooltip, Space, Switch } from "antd";
import { ArrowLeftOutlined, FilePdfOutlined, InfoCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import ReportDom from "../../../../components/common/ReportDom";
import { getInstitutionApi } from "../../../../apiservice/setting";
import { purchaseOrderVouchersApi } from "../../../../apiservice/order";

import PurchaseOrderVoucherBody from "./components/PurchaseOrderVoucherBody";
import PurchaseOrderVoucherBodyDesignA from "./components/PurchaseOrderVoucherBodyDesignA";
import PurchaseOrderVoucherBodyMobile from "./components/PurchaseOrderVoucherBodyMobile";
import PurchaseOrderCorporateVoucher from "./components/PurchaseOrderCorporateVoucher";

import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import Swal from "sweetalert2";
import { downloadPdf } from "../../../../lib/functions";
import { useShareDocument } from "../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../components/ShareButton/ShareDocumentButton";

const { Text } = Typography;

const PurchaseOrderVoucher = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const authInfo = useSelector(selectAuthInfo);

  const [loading, setLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [vouchersList, setVouchersList] = useState([]);
  const [institution, institutionSet] = useState(null);
  const [showStamp, setShowStamp] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [voucherNo, setVoucherNo] = useState(null);
  const [accName, setAccName] = useState(null);
  const [supplierEmail, setSupplierEmail] = useState("");

  const {
    shareOpen,
    setShareOpen,
    email,
    setEmail,
    // loading,
    handleEmailShare,
    handleWhatsAppShare,
  } = useShareDocument();

  

  useEffect(() => {
    const fetchVouchers = async () => {
      setLoading(true);
      try {
        const res = await purchaseOrderVouchersApi({}); 
        setVouchersList(res.data || []);
      } catch {
        setVouchersList([]);
      } finally {
        setLoading(false);
      }
    };

    const loadInstitution = async () => {
      try {
        const res = await getInstitutionApi();
        institutionSet(res.data);
      } catch (err) {
        Swal.fire("Error", err.response?.data?.message || "Failed to load institution", "warning");
      }
    };

    loadInstitution();
    fetchVouchers();
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Purchase Order Voucher-${voucherNo ? voucherNo : ""}`,
    removeAfterPrint: true,
  });

  useEffect(() => {
      if (shareOpen && supplierEmail && !email) {
        setEmail(supplierEmail);
      }
    }, [shareOpen, supplierEmail, email, setEmail]); 

  return (
    <div className="p-4 voucher-wrapper" style={{ overflowX: "hidden", width: "100%" }}>

      {/* Header Row */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 8,
        }}
      >
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          {!screens.xs && "Back"}
        </Button>

        <div style={{ flex: screens.xs ? "1 1 100%" : 1, maxWidth: screens.xs ? "100%" : 400 }}>
          <SearchListAutoComplete
            entityType="voucher"
            placeholder="Purchase Order No"
            optionsList={(vouchersList || []).map((v) => ({
              sale_id: v.pur_o_id,
              display_text: v.display_text,
              raw: v,
            }))}
            style={{alignItems:"center"}}
            onSelect={(obj) => setSelectedVoucher(obj?.raw || null)}
            showAddNew={false}
          />
        </div>

        <Space align="center" wrap style={{ rowGap: 8 }}>
          <Switch
            checked={showStamp}
            onChange={setShowStamp}
            checkedChildren="Stamp"
            unCheckedChildren="Stamp"
          />

          <Switch
            checked={showSignature}
            onChange={setShowSignature}
            checkedChildren="Signature"
            unCheckedChildren="Signature"
          />
          <ShareDocumentButton
            disabled={!selectedVoucher}
            screens={screens}
            shareOpen={shareOpen}
            setShareOpen={setShareOpen}
            email={email}
            setEmail={setEmail}
            loading={loading}
            onEmail={() =>
              handleEmailShare({
                url: `/print/purchase-order/${selectedVoucher.pur_o_id}`,
                fileName: `PurchaseOrder_${voucherNo || selectedVoucher.display_text}.pdf`,
                name: accName || "",
                docNo: voucherNo || selectedVoucher.display_text,
              })
            }
            onWhatsApp={() =>
              handleWhatsAppShare({
                url: `/print/purchase-order/${selectedVoucher.pur_o_id}`,
                fileName: `PurchaseOrder_${voucherNo || selectedVoucher.display_text}.pdf`,
                text: `Hello ${accName || ""}, Your Invoice ${voucherNo || selectedVoucher.display_text} is ready. Download here:`,
              })
            }
          />
          <Tooltip title={selectedVoucher?.approval_status === "pending" ? "Pending approval — Download is locked until fully approved" : ""}>
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              disabled={!selectedVoucher || !voucherNo || selectedVoucher?.approval_status === "pending"}
              onClick={() =>
                downloadPdf(
                  `/print/purchase-order/${selectedVoucher.pur_o_id}`,
                  `PurchaseOrder_${voucherNo || selectedVoucher.display_text}.pdf`
                )
              }
            >
              {!screens.xs && "Download"}
            </Button>
          </Tooltip>
          <Tooltip title={selectedVoucher?.approval_status === "pending" ? "Pending approval — Print is locked until fully approved" : ""}>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              disabled={!selectedVoucher || selectedVoucher?.approval_status === "pending"}
            >
              {!screens.xs && "Print"}
            </Button>
          </Tooltip>

          <Tooltip
            placement="bottom"
            title={
              <div style={{ maxWidth: 260 }}>
                <strong>Print Tip</strong>
                <div style={{ marginTop: 6 }}>
                  For best results, please disable
                  <b> “Headers and Footers”</b> in your browser’s print settings.
                </div>
              </div>
            }
          >
            <InfoCircleOutlined
              style={{
                color: "#888",
                fontSize: 16,
                cursor: "pointer",
              }}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Voucher Card */}
      <Card className="shadow-md rounded-xl">
        {!selectedVoucher ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            {loading ? <Spin /> : <Text type="secondary">Search and select a voucher</Text>}
          </div>
        ) : authInfo.branch_company_type === "Information Technology" ? (
          <PurchaseOrderCorporateVoucher
            pur_o_id={selectedVoucher.pur_o_id}
            institution={institution}
            authInfo={authInfo}
            setAccName={setAccName}
            setVoucherNo={setVoucherNo}
            showStamp={showStamp}
            showSignature={showSignature}
            setSupplierEmail={setSupplierEmail}
          />
        ) : screens.xs ? (
          <PurchaseOrderVoucherBodyMobile
            pur_o_id={selectedVoucher.pur_o_id}
            institution={institution}
            showStamp={showStamp}
            showSignature={showSignature}
            setAccName={setAccName}
            setVoucherNo={setVoucherNo}
            setSupplierEmail={setSupplierEmail}
          />
        ) : (
          <PurchaseOrderVoucherBody
            pur_o_id={selectedVoucher.pur_o_id}
            institution={institution}
            showStamp={showStamp}
            showSignature={showSignature}
            setAccName={setAccName}
            setVoucherNo={setVoucherNo}
            setSupplierEmail={setSupplierEmail}
          />
        )}
      </Card>

      {/* Hidden print layout */}
      {selectedVoucher && (
        <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
          <ReportDom
            ref={reportRef}
            mode={institution?.print_template === "design_a" ? "no-title" : (showSignature ? "signatureOn": showStamp ? "signatureOn" : "")}
            // title="Purchase Order Voucher"
            renderTable={() =>
              authInfo.branch_company_type === "Information Technology" ? (
                // IT-industry companies use this dedicated corporate layout
                // regardless of print_template -- it's a separately tailored
                // template, not the generic Purchase Order body Design A's
                // adapter was built from.
                <PurchaseOrderCorporateVoucher
                  pur_o_id={selectedVoucher.pur_o_id}
                  institution={institution}
                  authInfo={authInfo}
                  showStamp={showStamp}
                  showSignature={showSignature}
                  printMode
                />
              ) : institution?.print_template === "design_a" ? (
                <PurchaseOrderVoucherBodyDesignA pur_o_id={selectedVoucher.pur_o_id} showStamp={showStamp} showSignature={showSignature} />
              ) : (
                <PurchaseOrderVoucherBody
                  pur_o_id={selectedVoucher.pur_o_id}
                  institution={institution}
                  showStamp={showStamp}
                  showSignature={showSignature}
                  printMode
                />
              )
            }
          />
        </div>
      )}

    </div>
  );
};

export default PurchaseOrderVoucher;
